/**
 * Danish Endurance — autoplay video arming
 *
 * Do NOT fold this into assets/theme.js. It lives here so a theme upgrade cannot
 * silently overwrite or conflict with it.
 *
 * Stock Concept v6.0.0 arms autoplay video only after the window 'load' event
 * (DeferredMedia.connectedCallback -> `this.on(window, 'load', ...)`). On
 * danishendurance.com 'load' does not fire within 120s — roughly 70 requests stay
 * pending, including Shop Pay / arrive-server iframe assets and cdn/wpm — so the
 * arming callback never runs. Because the <video> only exists inside a <template>
 * and is injected by loadContent(), no video element is ever created and every
 * autoplay video stays on its poster image indefinitely.
 *
 * This re-arms anything the theme has left unarmed, using only the element's
 * public API (`autoplayArmed`, `init()`, `play()`, `pause()`) plus the global
 * Motion, so it keeps working if the vendor internals change again.
 *
 * Performance characteristics are deliberately identical to stock:
 *   - the Data Saver / 2g opt-out is honoured
 *   - Motion.inView still defers loading until the element scrolls into view, so
 *     nothing offscreen is fetched
 *   - arming happens on idle, so it does not compete with critical resources
 *   - the poster image is still what paints, so LCP is unaffected
 */
(() => {
  'use strict';

  const isDataSaver = () => {
    const connection = navigator.connection;
    return !!connection && (connection.saveData || /2g/.test(connection.effectiveType || ''));
  };

  const armElement = (element) => {
    if (element.autoplayArmed || typeof element.init !== 'function') return;
    if (typeof Motion === 'undefined') return;

    element.autoplayArmed = true;

    if (element.hasAttribute('data-inview-full')) {
      // The stock branch for snippets/social-feed-card.liquid: play only while
      // ~fully visible, pause on any edge cut.
      //
      // Stock guards the play with `if (!this.paused)`, which is omitted here on
      // purpose: `paused` is a getter on theme.Animation (assets/theme.js:760),
      // never on a media element, so on a <video-media> it is always undefined
      // and `!undefined` always true. Behaviour is therefore identical today,
      // and dropping the guard avoids depending on that quirk. If the vendor
      // ever gives DeferredMedia a real `paused`, revisit this branch.
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        entry.intersectionRatio >= 0.99 ? element.play() : element.pause();
      }, { threshold: 0.99 });

      observer.observe(element);
      element._deAutoplayObserver = observer;
    }
    else {
      Motion.inView(element, element.init.bind(element));
    }
  };

  const armAll = () => {
    if (isDataSaver()) return;
    document.querySelectorAll('video-media[autoplay]').forEach(armElement);
  };

  const schedule = () => {
    'requestIdleCallback' in window
      ? requestIdleCallback(armAll, { timeout: 1000 })
      : setTimeout(armAll, 200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  }
  else {
    schedule();
  }

  // Media injected after first paint needs arming too, and stock cannot do it:
  // its fallback path is `document.readyState === 'complete'`, which is never
  // reached on a page whose 'load' event never fires. Quick view is the concrete
  // case — sections/main-product-modal.liquid renders the gallery with
  // enable_video_autoplay (a merchant toggle, default off), and the markup
  // arrives by fetch (assets/theme.js:8324).
  //
  // These are the theme's own documented events, so no vendor internals are
  // touched. armAll() is guarded by autoplayArmed, so re-running it is free.
  ['page:loaded',            // theme ready (fires once, from its DOMContentLoaded)
   'quickview:loaded',       // quick view product gallery
   'product-info:loaded',    // PDP re-render on variant change
   'recommendations:loaded', // recommendation sections
   'collection:reloaded'     // collection AJAX re-render
  ].forEach((eventName) => document.addEventListener(eventName, schedule));

  // Theme-editor section re-renders replace the markup, so re-arm on those.
  document.addEventListener('shopify:section:load', schedule);
})();
